import { releaseAiAssistantProxySessions } from 'services/aiAssistantChat/aiAssistantChat';
import { useLogoutMutation } from 'services/auth/auth';

import { reloadToEvictZoomChatSdk } from 'features/ZoomChat/clearZoomChatIdentity';

import { selectUser } from 'store';
import { clearUser } from 'store/user/userSlice';

import { getLoginService } from 'utils/loginService';
import { local, LOCAL_KEYS, session } from 'utils/storage';

import { clearPrograms } from 'store/programs/programsSlice';
import { useAppDispatch, useAppSelector, useMessages, useZoomCall } from '../index';

/**
 * Provides a logout handler that triggers the logout mutation and clears user-related state and storage.
 *
 * @returns Function that signs the user out, resets Zoom state when present, and cleans local storage.
 */
export const useLogout = () => {
  const { loggerClient, resetValuesToDefault, room } = useZoomCall();
  const [logout] = useLogoutMutation();
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const { clearChannels } = useMessages();

  const handleClearStore = async (cb?: () => void) => {
    // Diagnostics first, but never at the expense of teardown. `reportToGlobalTracing` is a
    // network call, and before it was wrapped a rejection skipped everything below it: a patient
    // leaving a video visit could be left with their session and Zoom chat identity intact.
    try {
      if (room) {
        await loggerClient?.reportToGlobalTracing();
      }
    } catch {
      // Best effort. Swallowed rather than rethrown so the teardown below is unconditional and
      // no rejection escapes to be reported as a failed sign-out.
    } finally {
      // In the `finally`, not after the await. This is what runs `room.leave()` and the recording
      // teardown, and a tracing rejection must not skip it. Wrapping the block in `try` was not
      // enough on its own: everything inside the `try` after the await still shared the
      // rejection's fate.
      if (room) {
        resetValuesToDefault();
      }
    }

    local.set(LOCAL_KEYS.LOGOUT_EVENT, 'logout' + Math.random());
    // Zoom's chat identity lives in localStorage, which session.clear() does not touch. It is
    // dropped by clearZoomChatIdentityMiddleware in response to this `clearUser`, together with
    // every other path that ends or replaces a session, rather than by a call here that would
    // only ever cover manual logout.
    dispatch(clearUser());
    clearChannels();
    dispatch(clearPrograms());
    session.clear();

    // Zoom's chat client is an ES module: removing its script tag does not unload it, so the SDK
    // keeps running in this document holding the signed-out patient's engagement, consumer id and
    // transcript — and because it owns the localStorage keys the middleware just cleared, it can
    // write them straight back. A fresh document is the only eviction available, so when the SDK
    // is loaded we hard-navigate and let the page load supersede the caller's SPA callback.
    if (reloadToEvictZoomChatSdk()) {
      return;
    }

    cb?.();
  };

  // The AI chat's proxy sessions close while the Convex sign-in still holds (LMD-1820), alongside
  // the platform logout; the store clears once both have settled.
  return (cb?: () => void) => {
    // The login service holds its own session in a cookie on its host (LMD-682); clearing the store
    // alone would leave it to restore the patient on the next load.
    void Promise.allSettled([
      releaseAiAssistantProxySessions(),
      logout(user.accessToken).unwrap(),
      getLoginService()?.signOut()
    ]).finally(() => handleClearStore(cb));
  };
};
